// PKGV.A3 (#794) — URL (de)serialization for the panel's filter/search
// state. V3 (locked): scoped to filter state on an ALREADY-EXPANDED
// panel — restoring which row is expanded from a cold URL needs
// host-page `expandedId` routing, filed as a follow-on, not this
// story's job. Uses native URLSearchParams via react-router's
// useSearchParams (already the repo's own pattern — RisksPage.tsx),
// not a third-party query-string library, per the issue's own ask.
//
// Real enum casing (EVIDENCE_LINK, not the issue's fictional lowercase
// example) — see Sprint-A3-recon.md §6.
//
// Default state (all present types/edges checked, empty search) always
// serializes to NO params — matches "empty/default state has no query
// params" (issue) and doubles as the "reset" projection: collapsing the
// panel, resetting filters, or re-anchoring (which resets filter state,
// L6) should all leave a clean URL, because they all return to this
// same default shape.

import type { PKGNodeEntityType, PKGEdgeType } from '@blueprint/shared';

export const PKG_TYPES_PARAM = 'pkgTypes';
export const PKG_EDGES_PARAM = 'pkgEdges';
export const PKG_SEARCH_PARAM = 'pkgSearch';

export interface PanelFilterState {
  visibleTypes: Set<PKGNodeEntityType>;
  visibleEdgeTypes: Set<PKGEdgeType>;
  searchQuery: string;
}

function isSameSet<T>(a: Set<T>, allOf: T[]): boolean {
  return a.size === allOf.length && allOf.every(v => a.has(v));
}

/**
 * Read initial filter state from the URL, falling back to "everything
 * present is visible, no search" (the real default) for anything absent
 * or invalid. Unknown tokens in pkgTypes/pkgEdges are dropped silently
 * (never crash on a hand-edited or stale URL) rather than rejecting the
 * whole param.
 *
 * R2 adversarial-pass fix: if EVERY token in a param is invalid for the
 * current anchor's present-types set, filtering them all out used to
 * leave an EMPTY Set rather than falling back to defaults as documented
 * above — contradicting this function's own stated contract. This is a
 * real, reachable case, not a contrived one: filter params in the URL
 * aren't scoped per-anchor (expandedId itself isn't URL-driven either —
 * #1899), so a URL carrying `pkgTypes=risk` from a PREVIOUS row can
 * persist (browser back/forward, or simply not having gone through a
 * collapse that would have cleared it) and then get read on a DIFFERENT
 * row's first-ever mount whose neighbourhood has no risk-type nodes at
 * all. Without this fallback, that row opens straight into "No nodes
 * match the current filters" with nothing the user did to cause it.
 * Falling back to defaults whenever the filtered result is empty (but
 * the raw param wasn't) treats "nothing survived" as itself invalid.
 */
export function readFilterStateFromUrl(
  params: URLSearchParams,
  presentTypes: PKGNodeEntityType[],
  presentEdgeTypes: PKGEdgeType[],
): PanelFilterState {
  const typesRaw = params.get(PKG_TYPES_PARAM);
  const edgesRaw = params.get(PKG_EDGES_PARAM);
  const searchRaw = params.get(PKG_SEARCH_PARAM);

  const parsedTypes = typesRaw
    ? typesRaw.split(',').filter((t): t is PKGNodeEntityType => presentTypes.includes(t as PKGNodeEntityType))
    : null;
  const visibleTypes = parsedTypes && parsedTypes.length > 0 ? new Set(parsedTypes) : new Set(presentTypes);

  const parsedEdgeTypes = edgesRaw
    ? edgesRaw.split(',').filter((t): t is PKGEdgeType => presentEdgeTypes.includes(t as PKGEdgeType))
    : null;
  const visibleEdgeTypes = parsedEdgeTypes && parsedEdgeTypes.length > 0 ? new Set(parsedEdgeTypes) : new Set(presentEdgeTypes);

  return {
    visibleTypes,
    visibleEdgeTypes,
    searchQuery: searchRaw ?? '',
  };
}

/**
 * Write filter state into a mutable URLSearchParams (caller passes the
 * result to react-router's setSearchParams). Default-equivalent state
 * (everything present checked, empty search) clears all three params
 * rather than writing them out redundantly.
 */
export function writeFilterStateToUrl(
  params: URLSearchParams,
  state: PanelFilterState,
  presentTypes: PKGNodeEntityType[],
  presentEdgeTypes: PKGEdgeType[],
): void {
  if (isSameSet(state.visibleTypes, presentTypes)) {
    params.delete(PKG_TYPES_PARAM);
  } else {
    params.set(PKG_TYPES_PARAM, Array.from(state.visibleTypes).sort().join(','));
  }

  if (isSameSet(state.visibleEdgeTypes, presentEdgeTypes)) {
    params.delete(PKG_EDGES_PARAM);
  } else {
    params.set(PKG_EDGES_PARAM, Array.from(state.visibleEdgeTypes).sort().join(','));
  }

  if (!state.searchQuery) {
    params.delete(PKG_SEARCH_PARAM);
  } else {
    params.set(PKG_SEARCH_PARAM, state.searchQuery);
  }
}

/** Clears all three params — used when the panel collapses (unmounts). */
export function clearFilterStateFromUrl(params: URLSearchParams): void {
  params.delete(PKG_TYPES_PARAM);
  params.delete(PKG_EDGES_PARAM);
  params.delete(PKG_SEARCH_PARAM);
}
